import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { testExecutionsGridConfig } from '@/features/executions';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import {
  CLONE_META_DETAIL_TEXT_SX as EXECUTION_META_DETAIL_TEXT_SX,
  CLONE_META_DETAILS_LINK_SX as EXECUTION_DETAILS_LINK_SX,
  TableMetaRow,
  TableMetaStack,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import {
  buildExecutionRunDetailPath,
  buildResultsFileDetailPath,
  buildSuiteDetailPath,
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import {
  buildTagColorByName,
  mergeTagColorByName,
  resolveEntityTags,
  resolveTagColor,
} from '@/features/shared/tags';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { formatDuration, formatStatusLabel } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { getStatusIconComponent, RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridSortModel,
  resolveStatusChipTone,
} from '@/ui/primitives';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from './testsColumns.coreColumns';
import { TableCellContainer } from './testsTableCells';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestExecutionDto } from '@flowqa/shared';
import type { QueryKey } from '@tanstack/react-query';

type TestExecutionsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestExecutionDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: TestExecutionDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: TestExecutionDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  disableLinks?: boolean;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  tagColorByName?: Partial<Record<string, TagColor>>;
  hideNameColumn?: boolean;
  runColumnFirst?: boolean;
};

const TestExecutionsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No test executions found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  disableLinks = false,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagColorByName = {},
  hideNameColumn = false,
  runColumnFirst = false,
}: TestExecutionsTableProps): ReactElement => {
  const columnWidths = tableColumnWidths.testExecutions;
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const resolvedTagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const mergedTagColorByName = useMemo(
    () => mergeTagColorByName(resolvedTagColorByName, tagColorByName),
    [resolvedTagColorByName, tagColorByName],
  );
  const columns = useMemo<Array<GridColDef<TestExecutionDto>>>(
    () => [
      {
        field: 'name',
        headerName: 'Test',
        flex: columnWidths.name.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'tests-table-clone-test-cell',
        valueGetter: (_value, row) => row.testName ?? row.name ?? row.testId,
        renderCell: (params) => {
          const primarySuite = params.row.suiteName?.trim() ? params.row.suiteName : null;
          const testHref = disableLinks
            ? null
            : buildTestDetailPath(params.row.testSlug ?? params.row.testId);
          const suiteHref = disableLinks ? null : buildSuiteDetailPath(params.row.suiteSlug);
          const tags = resolveEntityTags(params.row);
          const resolvedTags = tags.map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, mergedTagColorByName),
          }));
          return (
            <TableCellContainer gap="none">
              <TableNameCell
                primary={{
                  label: params.row.testName ?? params.row.name ?? params.row.testId,
                  ...(testHref
                    ? {
                        href: testHref,
                        onClick: () => {
                          setSelectedProjectId(params.row.projectId);
                        },
                      }
                    : {}),
                }}
                secondary={
                  primarySuite
                    ? {
                        label: primarySuite,
                        ...(suiteHref
                          ? {
                              href: suiteHref,
                              onClick: () => {
                                setSelectedProjectId(params.row.projectId);
                              },
                            }
                          : {}),
                      }
                    : null
                }
                tags={resolvedTags}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'runNumber',
        headerName: 'Run',
        flex: columnWidths.run.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'clone-run-link-cell',
        renderCell: (params) => {
          const runLabel =
            typeof params.row.runNumber === 'number' ? `#${params.row.runNumber}` : 'View';
          const href =
            buildTestRunPath(params.row.testSlug ?? params.row.testId, params.row.runNumber) ??
            '/tests';
          return (
            <TableCellContainer gap="none">
              <RunLinkCell
                label={runLabel}
                href={disableLinks ? null : href}
                latest={params.row.isLatest === true}
                onClick={() => {
                  setSelectedProjectId(params.row.projectId);
                }}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: columnWidths.status.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'clone-execution-status-cell',
        renderCell: (params) => {
          const status = params.row.status;
          const statusTone = resolveStatusChipTone(status);
          const statusLabel = formatStatusLabel(status) || '-';
          const testRunHref = disableLinks
            ? null
            : buildTestRunPath(params.row.testSlug ?? params.row.testId, params.row.runNumber);
          const durationLabel = formatDuration(params.row.durationMs ?? null);
          const timestamp = params.row.timestamp;
          const timestampLabel = formatRelativeTime(timestamp);
          const timestampTitle = formatDate(timestamp, { includeTime: true });
          const resolvedTimestampTitle = timestampTitle || timestamp.toISOString();
          return (
            <TableCellContainer gap="none">
              <TableStatusMetaContent
                statusLabel={statusLabel}
                statusHref={testRunHref}
                onStatusClick={(event) => {
                  event.stopPropagation();
                  setSelectedProjectId(params.row.projectId);
                }}
                chipTone={resolveStatusChipTone(status)}
                chipIcon={<Box component={getStatusIconComponent(status)} />}
                chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
                chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
                durationLabel={durationLabel}
                durationIcon={
                  <TimerOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                }
                timestampLabel={timestampLabel}
                timestampTitle={resolvedTimestampTitle}
                timestampIcon={
                  <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                }
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'runId',
        headerName: 'Details',
        flex: columnWidths.details.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'clone-meta-details-cell',
        renderCell: (params) => {
          const uploadedLabel = formatRelativeTime(params.row.createdAt);
          const uploadedTitle = formatDate(params.row.createdAt, { includeTime: true });
          const resultsFileLabel =
            params.row.resultsFile?.fileName ?? params.row.resultsFileId ?? '-';
          const resultsFileHref = buildResultsFileDetailPath(params.row.resultsFileId);
          return (
            <TableCellContainer gap="none">
              <TableMetaStack rowGap={s('sm')}>
                <TableMetaRow
                  title="Execution Run"
                  icon={
                    <PlayCircleFilledWhiteOutlinedIcon
                      sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                    />
                  }
                  value={params.row.runId}
                  href={buildExecutionRunDetailPath(params.row.runId)}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                />
                <TableMetaRow
                  title="Results File"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  value={resultsFileLabel}
                  href={resultsFileHref}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  textSx={EXECUTION_META_DETAIL_TEXT_SX}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                />
                <TableMetaRow
                  title="Uploaded"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  value={uploadedLabel || '-'}
                  valueTitle={uploadedTitle || ''}
                  textSx={EXECUTION_META_DETAIL_TEXT_SX}
                />
              </TableMetaStack>
            </TableCellContainer>
          );
        },
      },
    ],
    [disableLinks, mergedTagColorByName, setSelectedProjectId],
  );
  const resolvedColumns = useMemo(() => {
    const visibleColumns = hideNameColumn
      ? columns.filter((column) => column.field !== 'name')
      : columns;
    if (!runColumnFirst) {
      return visibleColumns;
    }
    const runColumn = visibleColumns.find((column) => column.field === 'runNumber');
    const remainingColumns = visibleColumns.filter((column) => column.field !== 'runNumber');
    return runColumn ? [runColumn, ...remainingColumns] : visibleColumns;
  }, [columns, hideNameColumn, runColumnFirst]);
  const togglableColumnFields = useMemo(
    () => resolvedColumns.map((column) => column.field).filter((field) => field !== 'name'),
    [resolvedColumns],
  );
  const gridConfig = testExecutionsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'test-executions-table-clone' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const gridProps = buildTableGridProps<TestExecutionDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    showToolbar,
    chrome: 'embedded',
  });

  return (
    <ServerDataTable<TestExecutionDto>
      queryPath="test-executions/query"
      {...gridConfig}
      {...gridProps}
      columns={resolvedColumns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      onQueryMeta={onQueryMeta}
      search={search}
      columnVisibilityModel={columnVisibilityModel}
      disableColumnMenu={disableColumnMenu}
      disableColumnSorting={disableColumnSorting}
      disableCellFocus
      getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
      skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
      showSkeletonOnFetch
      autoHeight
      gridProps={{
        onColumnVisibilityModelChange,
        slotProps: {
          toolbar: {
            showQuickFilter: true,
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
          columnsManagement: {
            disableShowHideToggle: true,
            disableResetButton: true,
            getTogglableColumns: () => togglableColumnFields,
          },
        },
      }}
      sx={[
        ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
        {
          '& .MuiDataGrid-cell[data-field="status"]': {
            alignItems: 'flex-start',
          },
          '& .MuiDataGrid-cell[data-field="runNumber"], & .MuiDataGrid-cell[data-field="runId"]': {
            alignItems: 'flex-start',
          },
        },
      ]}
    />
  );
};

export const TestExecutionsTable = memo(TestExecutionsTableComponent);
